// src/components/FileList.js
import React, { useState, useEffect } from 'react';
import { getAllFiles, deleteFile } from '../firebase/storage';

const FileList = ({ onDeleteFile, isLoading, isAuthenticated, currentUserId }) => {
  const [files, setFiles] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load all files, not just the user's
  useEffect(() => {
    const loadAllFiles = async () => {
      if (!isAuthenticated) {
        setLocalLoading(false);
        return;
      }
      
      setLocalLoading(true);
      try {
        const allFiles = await getAllFiles();
        setFiles(allFiles);
      } catch (err) {
        console.error("Error loading files:", err);
        setError("Failed to load files");
      } finally {
        setLocalLoading(false);
      }
    };
    
    loadAllFiles();
  }, [isAuthenticated]);
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) {
      return bytes + ' B';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    }
  };
  
  // Format upload date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  // Determine file type icon
  const getFileTypeIcon = (contentType) => {
    if (contentType.includes('pdf')) {
      return 'PDF';
    } else if (contentType.includes('word') || contentType.includes('document')) {
      return 'DOC';
    } else if (contentType.includes('image')) {
      return 'IMG';
    } else if (contentType.includes('spreadsheet') || contentType.includes('excel')) {
      return 'XLS';
    } else if (contentType.includes('presentation') || contentType.includes('powerpoint')) {
      return 'PPT';
    } else {
      return 'FILE';
    }
  };
  
  if (!isAuthenticated) {
    return (
      <div className="file-list">
        <h3>All Files</h3>
        <div className="sign-in-prompt">
          <p>Please sign in to view files</p>
        </div>
      </div>
    );
  }
  
  const isLoaderVisible = isLoading || localLoading;
  
  return (
    <div className="file-list">
      <h3>All Files</h3>
      
      {error && <div className="error-message">{error}</div>}
      
      {isLoaderVisible ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading files...</p>
        </div>
      ) : files.length === 0 ? (
        <div className="no-files">
          <p>No files uploaded yet.</p>
        </div>
      ) : (
        <div className="files-table-container">
          <table className="files-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Size</th>
                <th>Uploaded By</th>
                <th>Upload Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.id}>
                  <td className="file-type-cell">
                    <span className="file-type-badge">
                      {getFileTypeIcon(file.contentType)}
                    </span>
                  </td>
                  <td className="file-name-cell">{file.name}</td>
                  <td>{formatFileSize(file.size)}</td>
                  <td>{file.uploaderName || 'Unknown'}</td>
                  <td>{formatDate(file.uploadedAt)}</td>
                  <td className="actions-cell">
                    <a 
                      href={file.downloadUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="download-button"
                      download
                    >
                      Download
                    </a>
                    
                    {/* Only show delete button if user owns the file */}
                    {currentUserId === file.userId && (
                      <button 
                        onClick={() => onDeleteFile(file.id)}
                        className="delete-button"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FileList;